<!-- PatientListWithVitalSigns.vue -->
<template>
  <VContainer>
    <div class="flex flex-col flex-1">
      <PatientOverviewHeader
        :current-display-mode="currentDisplayMode"
        :selected-sort-value="vitalSignsSortBy"
        :total-cases="totalCases"
        :filtered-cases="filteredCases.length"
        :is-aki-enabled="isAkiEnabled"
        :can-add-patient="canAddPatient"
        :very-high-risk-aki-patients="veryHighRiskAkiPatientsCount"
        :high-risk-aki-patients="highRiskAkiPatientsCount"
        :is-aki-loading="isAkiLoading"
        :active-widget="activeWidget"
        @add-patient-click="navigateToAddPatient"
        @update-display-mode="updateDisplayMode"
        @widget-selected="onWidgetSelected"
      />

      <VAlert
        v-if="activeWidget === WidgetType.AKI_PATIENTS && akiWidgetReady"
        color="gray-700"
        variant="outlined"
        rounded="lg"
        class="mb-4 text-body-2 bg-white font-weight-medium"
        style="border-color: #d1d5db"
        density="compact"
      >
        <template #prepend>
          <span class="featured-icon-outline">
            <VIcon icon="custom:alertTriangle" size="20" />
          </span>
        </template>
        <div>
          <i18n-t keypath="AKI could not be predicted for {patients}." tag="span">
            <template #patients>
              <span class="font-semibold">{{
                t(
                  '{count} patients | {count} patient | {count} patients',
                  { count: couldNotPredictCount },
                  couldNotPredictCount
                )
              }}</span>
            </template>
          </i18n-t>
          <button
            type="button"
            @click="() => openPatientModal()"
            class="font-semibold text-consultation-card cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-consultation-card bg-transparent p-0 border-0 font-[inherit]"
            :disabled="couldNotPredictCount <= 0"
          >
            &nbsp;&nbsp;{{ t('show patients') }}
          </button>
        </div>
      </VAlert>

      <div v-if="isInitialVitalSignsLoading" class="my-2">
        <VProgressLinear indeterminate />
      </div>

      <div v-else>
        <div
          v-if="
            (activeWidget === WidgetType.ALL_PATIENTS &&
              (filteringInProgress || vitalSignsLoading)) ||
            (activeWidget === WidgetType.AKI_PATIENTS && isAkiLoading)
          "
          class="my-2"
        >
          <VProgressLinear indeterminate />
        </div>

        <div
          v-if="activeWidget === WidgetType.AKI_PATIENTS && !hasAcceptedAkiLimitation"
          class="w-full"
        >
          <AkiSidebar
            class="w-full h-fit"
            @close-sidebar="onWidgetSelected(WidgetType.ALL_PATIENTS)"
          />
        </div>

        <div v-else class="flex gap-4 relative">
          <AllPatientsWidget
            v-if="activeWidget === WidgetType.ALL_PATIENTS"
            :filtered-cases="filteredCases"
            :vital-signs-display-mode="vitalSignsDisplayMode"
            :vital-signs-sort-by="vitalSignsSortBy"
            :is-sidebar-visible="isEdsSidebarVisible"
            :more-cases-loading="moreCasesLoading"
            @open-eds-sidebar="openEdsSidebar"
            @sort-requested="handleSort"
            @edit-patient="onEditPatient"
            @close-case="onCloseCase"
          />

          <AkiPatientsWidget
            v-else-if="activeWidget === WidgetType.AKI_PATIENTS"
            :display-mode="vitalSignsDisplayMode.display"
            :is-eds-sidebar-visible="isEdsSidebarVisible"
            :is-aki-sidebar-visible="isAkiSidebarVisible"
            :aki-sort-by="akiSortBy"
            :aki-sort-direction="akiSortDirection"
            @open-eds-sidebar="openEdsSidebar"
            @open-aki-sidebar="openAkiSidebar"
            @sort-requested="handleAkiSort"
            @edit-patient="onEditPatient"
            @close-case="onCloseCase"
          />

          <div
            v-if="isEdsSidebarVisible"
            :class="xs ? 'w-full' : 'relative min-w-[320px] min-h-[400px]'"
          >
            <EdsSidebar
              class="w-full h-fit"
              :class="{ 'absolute max-h-full': !xs }"
              @close-sidebar="isEdsSidebarVisible = false"
            />
          </div>

          <div
            v-if="isAkiSidebarVisible"
            :class="xs ? 'w-full' : 'relative min-w-[320px] min-h-[400px]'"
          >
            <AkiSidebar
              class="w-full h-fit"
              :class="{ 'absolute max-h-full': !xs }"
              @close-sidebar="isAkiSidebarVisible = false"
            />
          </div>
        </div>
      </div>

      <div
        v-if="
          !isInitialVitalSignsLoading &&
          (activeWidget === WidgetType.ALL_PATIENTS || activeWidget === WidgetType.AKI_PATIENTS)
        "
        class="flex flex-col items-center gap-4 py-4"
      >
        <span
          v-if="
            activeWidget === WidgetType.ALL_PATIENTS && noCasesAfterFiltering && !vitalSignsLoading
          "
          data-testid="no-cases-after-filtering-message"
          class="text-body-2 text-secondary-text"
        >
          {{ t("No results. Please ensure that you haven't applied too many filters.") }}
        </span>

        <div
          v-if="
            (activeWidget === WidgetType.ALL_PATIENTS &&
              (!noCasesAfterFiltering || vitalSignsLoading)) ||
            (activeWidget === WidgetType.AKI_PATIENTS &&
              totalAkiPatientsCount &&
              totalAkiPatientsCount > 0)
          "
          class="flex justify-between items-center w-full px-6 py-3"
        >
          <VBtn
            data-testid="prev-page-btn"
            class="border-gray-200 font-semibold"
            variant="outlined"
            :disabled="
              activeWidget === WidgetType.ALL_PATIENTS ? currentPage <= 1 : akiCurrentPage <= 1
            "
            @click="
              changePage(
                activeWidget === WidgetType.ALL_PATIENTS ? currentPage - 1 : akiCurrentPage - 1
              )
            "
          >
            <template #prepend>
              <VIcon class="text-[20px]" color="gray-400">arrow_back</VIcon>
            </template>
            {{ t('Previous page') }}
          </VBtn>

          <VPagination
            data-testid="pagination"
            v-model="paginationPage"
            :length="
              activeWidget === WidgetType.ALL_PATIENTS
                ? Math.ceil(totalCases / itemsPerPage)
                : Math.ceil((totalAkiPatientsCount ?? 0) / akiItemsPerPage)
            "
            :total-visible="xs ? 3 : 8"
            class="pagination"
            color="gray-500"
          />

          <VBtn
            data-testid="next-page-btn"
            class="border-gray-200 font-semibold"
            variant="outlined"
            :disabled="
              activeWidget === WidgetType.ALL_PATIENTS
                ? currentPage >= Math.ceil(totalCases / itemsPerPage)
                : akiCurrentPage >= Math.ceil((totalAkiPatientsCount ?? 0) / akiItemsPerPage)
            "
            @click="
              changePage(
                activeWidget === WidgetType.ALL_PATIENTS ? currentPage + 1 : akiCurrentPage + 1
              )
            "
          >
            {{ t('Next page') }}
            <template #append>
              <VIcon class="text-[20px]" color="gray-400">arrow_forward</VIcon>
            </template>
          </VBtn>
        </div>

        <PatientsWithErrorsModal
          v-if="showPatientsWithErrorsModal"
          v-model:show="showPatientsWithErrorsModal"
          :patient-errors="couldNotPredictAkiErrors"
        />

        <span
          v-if="
            activeWidget === WidgetType.AKI_PATIENTS &&
            totalAkiPatientsCount &&
            totalAkiPatientsCount > 0 &&
            isOnLastAkiPage
          "
          class="text-center text-xs text-gray-500"
        >
          {{
            t(
              'All patients with a known high or very high AKI risk are shown. Not being shown does not mean there is no AKI risk.'
            )
          }}
        </span>
      </div>
    </div>
  </VContainer>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useDisplay } from 'vuetify/framework'

import PatientOverviewHeader from '@/components/patient/components/organisms/PatientOverviewHeader.vue'
import EdsSidebar from '../molecules/EdsSidebar.vue'

import { usePatientsWithVitalSignsStore } from '@/stores/patientsWithVitalSigns'
import { useAkiPredictionsStore } from '@/stores/akiPredictions'
import { useUserStore } from '@/stores/user'
import { usePluginsStore } from '@/stores/plugins'
import { useNoticeStore } from '@/stores/notice'
import useRoutingAvailability from '@/composables/useRoutingAvailability'
import { AdminPatientsService } from '@/hospitalAdminTool/services/AdminPatientsService'

import { WidgetType } from '@/enums/WidgetType'
import { DisplayMode } from '@/stores/models/VitalSignsDisplayMode'
import { NoticeType } from '@/stores/models/Notice'
import {
  AllCaseOrderOptionsDTO,
  CasesWithVitalSignsFilterDTOSortDirectionEnum,
  Discharge
} from '@/generated'
import { routeNames } from '@/router/routeNames'
import AllPatientsWidget from '@/components/patient/components/molecules/AllPatientsWidget.vue'
import AkiPatientsWidget from '@/components/patient/components/molecules/AkiPatientsWidget.vue'
import AkiSidebar from '@/components/patient/components/molecules/AkiSidebar.vue'
import { useAkiPlugin } from '@/composables/useAkiPlugin'
import PatientsWithErrorsModal from '@/components/patient/components/molecules/PatientsWithErrorsModal.vue'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const { xs } = useDisplay()

const { hasHospitalPhysicianRole, hasAddPatientsPermissions, hasWardWithoutPDMS } = useUserStore()
const { isAkiEnabled, isAkiEnabledLoading } = storeToRefs(usePluginsStore())
const { hasAcceptedAkiLimitation } = useAkiPlugin()
const noticeStore = useNoticeStore()

// Stores
const patientsWithVitalSignsStore = usePatientsWithVitalSignsStore()
const akiPredictionsStore = useAkiPredictionsStore()

const {
  vitalSignsLoading,
  moreCasesLoading,
  casesWithVitalSigns,
  vitalSignsDisplayMode,
  vitalSignsSortBy,
  totalCases,
  filteringInProgress,
  currentPage,
  itemsPerPage
} = storeToRefs(patientsWithVitalSignsStore)

const {
  veryHighRiskAkiPatientsCount,
  highRiskAkiPatientsCount,
  isAkiLoading,
  totalAkiPatientsCount,
  couldNotPredictCount,
  couldNotPredictAkiErrors,
  currentPage: akiCurrentPage,
  itemsPerPage: akiItemsPerPage,
  sortBy: akiSortBy,
  sortDirection: akiSortDirection
} = storeToRefs(akiPredictionsStore)

const { routingAvailabilityPerCaseId } = useRoutingAvailability()

// State
const isInitialLoadDone = ref(false)
const isInitialVitalSignsLoading = ref(true)
const isEdsSidebarVisible = ref(false)
const isAkiSidebarVisible = ref(false)
const activeWidget = ref<WidgetType>(
  route.query.widget === WidgetType.AKI_PATIENTS ? WidgetType.AKI_PATIENTS : WidgetType.ALL_PATIENTS
)
const showPatientsWithErrorsModal = ref(false)
const currentDisplayMode = computed(() => vitalSignsDisplayMode.value.display)
const canAddPatient = computed(
  () => hasHospitalPhysicianRole && hasAddPatientsPermissions && hasWardWithoutPDMS
)

const casesWithUpdatedRoutingAvailability = computed(() => {
  return casesWithVitalSigns.value.map((caseItem) => ({
    ...caseItem,
    case: {
      ...caseItem.case,
      emergencyRoutingUnavailable: routingAvailabilityPerCaseId.value.has(caseItem.case.id)
        ? !routingAvailabilityPerCaseId.value.get(caseItem.case.id)?.emergency
        : true
    }
  }))
})

// Filtering is applied server-side, so the loaded cases are already filtered.
const filteredCases = computed(() => casesWithUpdatedRoutingAvailability.value)

const noCasesAfterFiltering = computed(() => filteredCases.value.length === 0)

const isOnLastAkiPage = computed(
  () =>
    akiCurrentPage.value >= Math.ceil((totalAkiPatientsCount.value ?? 0) / akiItemsPerPage.value)
)

const paginationPage = computed({
  get() {
    return activeWidget.value === WidgetType.ALL_PATIENTS ? currentPage.value : akiCurrentPage.value
  },
  set(value: number) {
    changePage(value)
  }
})

// Initialization
onMounted(() => {
  patientsWithVitalSignsStore.initVitalSignsDisplayOptions()
})

onMounted(async () => {
  if (!isInitialLoadDone.value) {
    isInitialVitalSignsLoading.value = true
    await patientsWithVitalSignsStore.getCasesWithVitalSigns()
    isInitialVitalSignsLoading.value = false
    isInitialLoadDone.value = true
  } else {
    await patientsWithVitalSignsStore.getCasesWithVitalSigns()
  }
})

// AKI predictions must not be fetched until the user has accepted the AKI
// contraindications (MDR compliance). Once accepted, AkiSidebar triggers the
// initial load; this watch covers the flag flipping while the list is mounted
// (e.g. acceptance synced from another tab).
onMounted(() => {
  if (akiWidgetReady.value) {
    akiPredictionsStore.getAkiPredictionsForUser()
  }
})

const akiWidgetReady = computed(() => {
  return isAkiEnabled.value && hasAcceptedAkiLimitation.value && !isLoading.value
})

const isLoading = computed(() => {
  return isAkiEnabledLoading.value || isAkiLoading.value
})

const canFetchAkiPredictions = computed(() => {
  return akiWidgetReady.value && totalAkiPatientsCount.value === null
})

watch(
  canFetchAkiPredictions,
  (isReadyToFetch) => {
    if (isReadyToFetch) {
      akiPredictionsStore.getAkiPredictionsForUser()
    }
  },
  { immediate: true }
)

function openPatientModal() {
  showPatientsWithErrorsModal.value = true
}

// Methods
async function changePage(newPage: number) {
  if (activeWidget.value === WidgetType.ALL_PATIENTS) {
    currentPage.value = newPage
    await patientsWithVitalSignsStore.getCasesWithVitalSigns()
  } else if (activeWidget.value === WidgetType.AKI_PATIENTS) {
    akiCurrentPage.value = newPage
    await akiPredictionsStore.getAkiPredictionsForUser(newPage, akiItemsPerPage.value)
  }

  window.scrollTo({ top: 0, behavior: 'smooth' })
}

function onWidgetSelected(widget: WidgetType) {
  activeWidget.value = widget
  router.replace({ query: { ...route.query, widget } })
}

function navigateToAddPatient() {
  router.push({ name: routeNames.ADD_PATIENT })
}

// Only one side panel may be open at a time - opening one closes the other.
function openEdsSidebar() {
  isAkiSidebarVisible.value = false
  isEdsSidebarVisible.value = true
}

function openAkiSidebar() {
  isEdsSidebarVisible.value = false
  isAkiSidebarVisible.value = true
}

function updateDisplayMode(displayMode: DisplayMode) {
  vitalSignsDisplayMode.value = { display: displayMode }
  patientsWithVitalSignsStore.commitVitalSigns()
}

function handleSort(key: AllCaseOrderOptionsDTO) {
  if (vitalSignsSortBy.value.sortBy === key) {
    vitalSignsSortBy.value = {
      sortBy: key,
      sortDirection:
        vitalSignsSortBy.value.sortDirection ===
        CasesWithVitalSignsFilterDTOSortDirectionEnum.ASCENDING
          ? CasesWithVitalSignsFilterDTOSortDirectionEnum.DESCENDING
          : CasesWithVitalSignsFilterDTOSortDirectionEnum.ASCENDING
    }
  } else {
    vitalSignsSortBy.value = {
      sortBy: key,
      sortDirection: CasesWithVitalSignsFilterDTOSortDirectionEnum.ASCENDING
    }
  }
  patientsWithVitalSignsStore.commitVitalSigns()
}

async function handleAkiSort(key: string) {
  if (akiSortBy.value === key) {
    akiSortDirection.value = akiSortDirection.value === 'ASC' ? 'DESC' : 'ASC'
  } else {
    akiSortBy.value = key
    akiSortDirection.value = 'ASC'
  }

  await akiPredictionsStore.getAkiPredictionsForUser(1, akiItemsPerPage.value)
}

function onEditPatient(caseId: string) {
  router.push({ name: routeNames.EDIT_PATIENT, params: { caseId } })
}

async function onCloseCase(caseId: string, reason: Discharge) {
  try {
    await AdminPatientsService.closeCase(caseId, reason)
    noticeStore.add({ type: NoticeType.SUCCESS, message: t('Case closed successfully') })
    if (activeWidget.value === WidgetType.AKI_PATIENTS) {
      await akiPredictionsStore.getAkiPredictionsForUser(
        akiCurrentPage.value,
        akiItemsPerPage.value
      )
    } else {
      await patientsWithVitalSignsStore.getCasesWithVitalSigns()
    }
  } catch (error) {
    noticeStore.add({ type: NoticeType.ERROR, message: t('Failed to close case') })
  }
}
</script>

<style scoped>
:deep(.pagination) {
  .v-pagination__prev,
  .v-pagination__next {
    display: none !important;
  }

  .v-pagination__item {
    margin: 0 2px;

    & > button {
      border-radius: 8px;
      min-width: unset;
      width: 40px;
      height: 40px;
      font-weight: 600;
    }
  }

  .v-pagination__item--is-active {
    & > button {
      color: rgb(var(--v-theme-gray-700)) !important;
    }
  }
}

.featured-icon-outline {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  color: rgb(var(--v-theme-gray-700));
  margin: 12px;
}

.featured-icon-outline::before,
.featured-icon-outline::after {
  content: '';
  position: absolute;
  border-radius: 50%;
}

.featured-icon-outline::before {
  inset: -6px;
  border: 3px solid rgb(var(--v-theme-gray-300));
}

.featured-icon-outline::after {
  inset: -12px;
  border: 3px solid rgb(var(--v-theme-gray-100));
}
</style>
